import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ArrowUpDown, MessageCircleMore } from 'lucide-react'
import Card from 'app/frontend/base/components/Card'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import capitalize from 'lodash/capitalize'
import EventParticipantContextMenu from './EventParticipantContextMenu'
import { cn } from '@/lib/utils'
import { useTeamEventPlanningInfoContext } from '../context/TeamEventPlanningInfoContext'
import EventActivityParticipantPlanningInfo from './EventActivityParticipantPlanningInfo'
import EventActivityParticipantActivityPlanningInfo from './EventActivityParticipantActivityPlanningInfo'

type Props = {
  entry: EventActivityParticipantEntry
  activityId: number
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  canAdminEdit: boolean
  variant: 'selected' | 'benched' | 'unavailable'
}

const EventActivitySetupParticipant = ({
  entry,
  activityId,
  selectableStatuses,
  canAdminEdit,
  variant,
}: Props) => {
  const { updateSelection } = useTeamEventUpdateContext()
  const { showPlanningInfo } = useTeamEventPlanningInfoContext()
  const {
    data: { user },
  } = usePageInfo()
  const selection = entry.selections[activityId]
  const displayCharacter = resolveEventParticipantCharacter(entry, activityId)
  const isOwnCharacter =
    user?.id === entry.participant.characterReference.userId
  const canEditStatus = canAdminEdit || isOwnCharacter
  const classSpecializations = getSpecializationsByClass(
    displayCharacter.classId,
  )
  const spec = selection.specId
    ? classSpecializations[selection.specId]
    : undefined
  const isSelected = variant === 'selected'
  const isUnavailable = variant === 'unavailable'
  const canToggleSelection = canAdminEdit && !isUnavailable
  const hasComment = Boolean(entry.participant.comment?.trim().length)

  return (
    <div className={cn('flex flex-col', isSelected ? 'w-full' : 'inline-flex')}>
      <Card
        className={
          isSelected
            ? 'flex items-center justify-between px-2 py-1 min-h-9 w-full'
            : 'inline-flex items-center justify-between px-1.5 py-0 text-xs text-muted-foreground gap-1.5'
        }
      >
        <span className='flex items-center gap-1 min-w-0'>
          <Tooltip>
            <TooltipTrigger className='inline-flex'>
              <span className='relative inline-flex'>
                {hasComment && !isSelected ? (
                  <MessageCircleMore className='w-4 h-4 text-accent-foreground' />
                ) : (
                  <>
                    {spec ? (
                      <BlizzardIcon
                        iconName={spec.icon}
                        size={isSelected ? undefined : 'small'}
                        className={isUnavailable ? 'grayscale' : undefined}
                      />
                    ) : (
                      <CharacterClassIcon
                        characterClass={displayCharacter.characterClass}
                        size={isSelected ? undefined : 'small'}
                        className={isUnavailable ? 'grayscale' : undefined}
                      />
                    )}
                  </>
                )}
                {hasComment && isSelected ? (
                  <span className='absolute -right-1 -bottom-1 rounded-full bg-card p-0.5'>
                    <MessageCircleMore className='w-4 h-4 text-accent-foreground' />
                  </span>
                ) : null}
              </span>
            </TooltipTrigger>
            {hasComment ? (
              <TooltipContent className='max-w-[300px] wrap-break-word'>
                {entry.participant.comment}
              </TooltipContent>
            ) : null}
          </Tooltip>
          <span
            className={
              isUnavailable
                ? 'truncate max-w-[9rem] text-muted-foreground'
                : `truncate max-w-[9rem] ${
                    classColours[displayCharacter.characterClass].text
                  }`
            }
          >
            {displayCharacter.name}
          </span>
        </span>
        <span
          className={
            isSelected
              ? 'flex items-center gap-0.5 ml-2'
              : 'flex items-center gap-0.5'
          }
        >
          <Tooltip>
            <TooltipTrigger className='mr-1'>
              <EventStatusIcon status={entry.participant.status} size='small' />
            </TooltipTrigger>
            <TooltipContent>
              {capitalize(entry.participant.status)}
            </TooltipContent>
          </Tooltip>
          {canToggleSelection ? (
            <Button
              variant='ghost'
              size='icon'
              className={isSelected ? 'h-6 w-6' : 'h-5 w-5'}
              onClick={() =>
                updateSelection(
                  entry.participant.id,
                  { selected: !isSelected },
                  activityId,
                )
              }
            >
              <ArrowUpDown className={isSelected ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
            </Button>
          ) : null}
          <EventParticipantContextMenu
            entry={entry}
            activityId={activityId}
            selectableStatuses={selectableStatuses}
            canEditStatus={canEditStatus}
            canAdminEdit={canAdminEdit}
          />
        </span>
      </Card>
      {showPlanningInfo && !isUnavailable ? (
        <Card
          className={cn(
            '-mt-[3px] rounded-t-none px-1.5 pt-[2px] pb-0.5 text-xs',
            isSelected ? 'w-full' : '',
          )}
        >
          <EventActivityParticipantPlanningInfo
            entry={entry}
            activityId={activityId}
            contentOnly
            showSelectedCount
          />
          <div className='mt-0.5 border-t border-border/70 pt-0.5'>
            <EventActivityParticipantActivityPlanningInfo
              entry={entry}
              activityId={activityId}
              contentOnly
            />
          </div>
        </Card>
      ) : null}
    </div>
  )
}

export default EventActivitySetupParticipant
